import type { GngDecision } from '@/stores/strategy-store'

/**
 * Single source of truth for Go/No-Go score semantics.
 * Criterion scores (1–5), category averages, and overall-score zones all
 * map onto four tones rendered with existing status tokens.
 */
export type GngTone = 'destructive' | 'warning' | 'neutral' | 'success'

/** Criterion score (1–5): 1 red, 2 amber, 3 neutral, 4–5 green. */
export function criterionTone(score: number): GngTone {
  if (score <= 1) return 'destructive'
  if (score === 2) return 'warning'
  if (score === 3) return 'neutral'
  return 'success'
}

/** Category average (1–5, one decimal). */
export function categoryAvgTone(avg: number): GngTone {
  if (avg < 2.0) return 'destructive'
  if (avg < 2.5) return 'warning'
  if (avg < 3.5) return 'neutral'
  return 'success'
}

/** Overall score (0–100) vs the assessment's decision thresholds. */
export function zoneTone(
  score: number,
  thresholds: { go: number; conditionalGo: number }
): GngTone {
  if (score >= thresholds.go) return 'success'
  if (score >= thresholds.conditionalGo) return 'warning'
  return 'destructive'
}

/**
 * Unweighted mean of scored criteria, rounded to one decimal.
 * Weights already shape the overall score; the per-category view answers
 * "how does this dimension look?" so every scored criterion counts equally.
 */
export function categoryAverage(criteria: { score: number | null }[]): number | null {
  const scores = criteria.flatMap((c) => (c.score != null ? [c.score] : []))
  if (scores.length === 0) return null
  const mean = scores.reduce((sum, s) => sum + s, 0) / scores.length
  return Math.round(mean * 10) / 10
}

/** "local_content" → "Local Content" (orphan categories removed from the template). */
export function humanizeCategorySlug(slug: string): string {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Sentence-case display for the built-in category names that are Title Case
 * ("Strategic Fit"). Category labels are org data — an org's own names are
 * shown exactly as typed; only the untouched defaults are adjusted.
 */
const DEFAULT_CATEGORY_DISPLAY: Record<string, string> = {
  'Strategic Fit': 'Strategic fit',
}

export function displayCategoryLabel(label: string): string {
  return DEFAULT_CATEGORY_DISPLAY[label] ?? label
}

export const RECOMMENDATION_LABEL: Record<GngDecision, string> = {
  go: 'Go',
  conditional_go: 'Conditional go',
  no_go: 'No go',
}

export const TONE_BAR_CLASS: Record<GngTone, string> = {
  destructive: 'bg-[hsl(var(--destructive))]',
  warning: 'bg-[hsl(var(--status-warning))]',
  neutral: 'bg-primary',
  success: 'bg-[hsl(var(--status-success))]',
}

/**
 * Faint fill for the score a user picked on a criterion.
 *
 * Deliberately a wash, not the solid `TONE_BAR_CLASS` fill: a criterion form is
 * fourteen of these down one page, and solid red/amber/green turns the page into
 * a traffic light. The judgement colour belongs to the category average and the
 * overall score, where it is read once. Here it is a hint, so the number stays
 * `text-foreground` and legible on top of it.
 */
export const TONE_WASH_CLASS: Record<GngTone, string> = {
  destructive: 'bg-[hsl(var(--destructive)/0.16)]',
  warning: 'bg-[hsl(var(--status-warning)/0.16)]',
  neutral: 'bg-[hsl(var(--primary)/0.16)]',
  success: 'bg-[hsl(var(--status-success)/0.16)]',
}

export const TONE_TEXT_CLASS: Record<GngTone, string> = {
  destructive: 'text-[hsl(var(--destructive))]',
  warning: 'text-[hsl(var(--status-warning))]',
  neutral: 'text-foreground',
  success: 'text-[hsl(var(--status-success))]',
}

export const TONE_PILL_CLASS: Record<GngTone, string> = {
  destructive: 'bg-[hsl(var(--destructive)/0.1)] text-[hsl(var(--destructive))]',
  warning: 'bg-[hsl(var(--status-warning)/0.12)] text-[hsl(var(--status-warning))]',
  neutral: 'bg-muted text-foreground',
  success: 'bg-[hsl(var(--status-success)/0.12)] text-[hsl(var(--status-success))]',
}

export interface ProjectableCriterion {
  score: number | null
  weight: number
  aiSuggestedScore: number | null
}

export interface ReviewableCriterion {
  score: number | null
  aiSuggestedScore: number | null
  aiRationale: string | null
}

/**
 * What the overall score would be if every AI suggestion were applied.
 *
 * Deliberately duplicates the backend's `recalculateScore` formula (weighted sum
 * over max possible, ×100, rounded) so the ghost figure on the score card and the
 * figure that lands after "Apply all" always agree. A suggestion overrides a manual
 * score, matching what the bulk apply actually writes.
 */
export function projectedScore(criteria: ProjectableCriterion[]): number | null {
  const effective = criteria
    .map((c) => ({ weight: c.weight, score: c.aiSuggestedScore ?? c.score }))
    .filter((c): c is { weight: number; score: number } => c.score != null)

  if (effective.length === 0) return null

  const weightedSum = effective.reduce((sum, c) => sum + c.score * c.weight, 0)
  const maxPossible = effective.reduce((sum, c) => sum + 5 * c.weight, 0)
  return Math.round((weightedSum / maxPossible) * 100)
}

/**
 * Criteria the AI scored at all — applied or not.
 *
 * Distinct from `pendingAiSuggestionCount` on purpose: the bar's headline claims
 * what the AI *did* ("AI suggested scores for 10 of 14 criteria"), which must stay
 * true after the user cherry-picks a few, while the button offers only what is
 * left to apply.
 */
export function aiSuggestionCount(criteria: ProjectableCriterion[]): number {
  return criteria.filter((c) => c.aiSuggestedScore != null).length
}

/** AI suggestions not currently in use — the "Apply all N" count. */
export function pendingAiSuggestionCount(criteria: ProjectableCriterion[]): number {
  return criteria.filter((c) => c.aiSuggestedScore != null && c.score !== c.aiSuggestedScore).length
}

/**
 * Criteria the AI assessed but declined to score for lack of evidence, and which
 * the user has not scored either — the work that is unambiguously theirs.
 */
export function needsManualReviewCount(criteria: ReviewableCriterion[]): number {
  return criteria.filter(
    (c) => c.aiRationale != null && c.aiSuggestedScore == null && c.score == null
  ).length
}
